import * as Yup from "yup";
import "./createBatlle.css";
import AddBox from "./AddBox";
import { routes } from "../../routes";
import { ChevronLeft } from "lucide-react";
import { useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlinePlus } from "react-icons/ai";
import { Formik, Form as FormikForm } from "formik";
import MyVerticallyCenteredModal from "./BattleModal";
import { postBattles } from "../../api/modules/b2droppie.class";
import TopUpModal from "../../components/TopupModal/TopUpModal";
import { allBattleBoxes, boxCategory } from "../../api/modules/b2droppie.class";
import BackdropLoader from "../../components/BackdropLoader/BackdropLoader";
import { Col, Container, Row, Form as BootstrapForm } from "react-bootstrap";
import { ReactComponent as Swords } from "../../assets/icons/swords.svg";
import Toaster from "../../components/Toast/Toast";

const CreateBoxBattle = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [totalBoxes, setTotalBoxes] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [selectedCards, setSelectedCards] = useState([]);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 769);

  // ✅ Ref to always access latest selectedCards inside async useEffect without stale closure
  const selectedCardsRef = useRef([]);
  useEffect(() => {
    selectedCardsRef.current = selectedCards;
  }, [selectedCards]);

  const typeOptions = [{ value: "option1", label: "1 * 1" }];
  const [topupmodalShow, setTopUpModalShow] = useState(false);
  const [error, setError] = useState({ success: "", error: "" });
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const [toastMessage, setToastMessage] = useState(null);

  // ── Measure navbar height dynamically ──
  useEffect(() => {
    const measureNavbar = () => {
      const navbar =
        document.querySelector("nav") ||
        document.querySelector(".navbar") ||
        document.querySelector("[class*='navbar']") ||
        document.querySelector("[class*='Navbar']") ||
        document.querySelector("header");

      if (navbar) {
        setNavbarHeight(navbar.offsetHeight);
      }
    };

    measureNavbar();

    // Re-measure on resize (responsive navbar height may change)
    window.addEventListener("resize", measureNavbar);
    return () => window.removeEventListener("resize", measureNavbar);
  }, []);

  useEffect(() => {
    try {
      const api = async () => {
        const allresponse = await allBattleBoxes();
        const allBoxes = allresponse?.data?.data || [];

        // ✅ Merges previously added quantities into new data so selections survive category changes
        const mergeQuantities = (boxes) =>
          boxes.map((box) => {
            const existing = selectedCardsRef.current.find((s) => s.id === box.id);
            return existing && existing.quantity > 0
              ? { ...box, quantity: existing.quantity }
              : box;
          });

        setData(mergeQuantities(allBoxes));
        setLoading(false);

        if (selected) {
          const categoryName = selected.value;
          const response = await boxCategory(categoryName);

          const categoryBoxes =
            response?.data?.data?.data?.[0]?.box ||
            response?.data?.data ||
            response?.data ||
            null;

          if (Array.isArray(categoryBoxes) && categoryBoxes.length > 0) {
            setData(mergeQuantities(categoryBoxes));
          } else {
            const filtered = allBoxes.filter(
              (item) => item?.box_category?.name === categoryName
            );
            setData(mergeQuantities(filtered.length > 0 ? filtered : allBoxes));
          }

          setLoading(false);
        }
      };

      api();
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  }, [selected]);

  const validationSchema = Yup.object().shape({
    private_battle: Yup.boolean().required("Toggle 1 is required"),
    bot: Yup.boolean().required("Toggle 2 is required"),
    crazy_mod: Yup.boolean().required("Toggle 3 is required"),
  });

  const initialValues = {
    private_battle: false,
    bot: false,
    crazy_mod: false,
  };

 const toggleRef = useRef({});

const handleToggleChange = (formik, field, value) => {
  const now = Date.now();
  if (toggleRef.current[field] && now - toggleRef.current[field] < 500) return; 
  toggleRef.current[field] = now;
  formik.setFieldValue(field, value);
};

  const updateOrCreateObject = (value) => {
    setSelectedCards((prevSelectedCards) => {
      const existingIndex = prevSelectedCards.findIndex(
        (item) => item?.id === value?.id
      );

      if (existingIndex !== -1) {
        const updatedCards = [...prevSelectedCards];
        updatedCards[existingIndex] = { ...value };
        return updatedCards;
      } else {
        return [...prevSelectedCards, value];
      }
    });
  };

  const getPrice = (item) => {
    const price = item?.box_price || item?.price || "0";
    return parseFloat(String(price).replace("$", "")) || 0;
  };

  const handlePlusClick = (itemId) => {
    if (!data) return;
    const index = data.findIndex((item) => item.id === itemId);
    if (index === -1) return;

    const newData = [...data];
    newData[index].quantity = (newData[index].quantity || 0) + 1;
    setData(newData);
    setTotalBoxes((prev) => prev + 1);
    setTotalPrice((prev) => prev + getPrice(newData[index]));
    updateOrCreateObject(newData[index]);
  };

  const handleMinusClick = (itemId) => {
    if (!data) return;
    const index = data.findIndex((item) => item.id === itemId);
    if (index === -1) return;

    const newData = [...data];
    if (newData[index].quantity === 1) {
      newData[index].quantity = 0;
      setData(newData);
      setTotalBoxes((prev) => prev - 1);
      setTotalPrice((prev) => prev - getPrice(newData[index]));
    } else if (newData[index].quantity > 1) {
      newData[index].quantity -= 1;
      setData(newData);
      setTotalBoxes((prev) => prev - 1);
      setTotalPrice((prev) => prev - getPrice(newData[index]));
    }
    updateOrCreateObject(newData[index]);
  };

  const handleAddToBattleClick = (itemId) => {
    if (!data) return;
    const index = data.findIndex((item) => item.id === itemId);
    if (index === -1) return;

    const newData = [...data];
    if (newData[index].quantity > 0) {
      newData[index].quantity += 1;
    } else {
      newData[index].quantity = 1;
    }
    setData(newData);
    setTotalBoxes((prev) => prev + 1);
    setTotalPrice((prev) => prev + getPrice(newData[index]));
    updateOrCreateObject(newData[index]);
  };

  const handleSubmit = async (values) => {
    setDisabled(true);
    setLoading(true);

    const box_ids = selectedCards
      .filter((item) => item?.quantity > 0)
      .flatMap((item) => {
        const ids = [];
        for (let i = 0; i < item.quantity; i++) {
          ids.push(item.id);
        }
        return ids;
      });

    try {
      const mode = values.crazy_mod ? "crazy" : "normal";
      const isPrivate = values.private_battle ? 1 : 0;

      const requestData = {
        box_ids,
        mode: mode,
        private: isPrivate,
      };

      const response = await postBattles(requestData);

      if (response.status === 200 && response.data?.battle) {
        setError((prev) => ({
          ...prev,
          success: response?.data?.message || "Battle created successfully!",
          error: "",
        }));
        navigate(routes.BATTLES);
      } else {
        setError((prev) => ({
          ...prev,
          success: "",
          error:
            response?.data?.error?.message ||
            response?.data?.message ||
            "Failed to create battle",
        }));
        setDisabled(false);
        setToastMessage({
          error:
            response?.data?.error?.message ||
            response?.data?.message ||
            "Failed to create battle",
        });
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || err?.message || "Something went wrong";

      setError((prev) => ({
        ...prev,
        success: "",
        error: errorMessage,
      }));

      setToastMessage({ error: errorMessage });
      setDisabled(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDisabled(totalBoxes <= 0 ? true : false);
  }, [totalBoxes]);

  const activeBoxCount = selectedCards.filter((item) => item?.quantity > 0).length;

  return (
    <div className={isDarkMode ? "page-wrapper dark-mode p-0" : "page-wrapper"}>
      <Toaster message={toastMessage} />

      {/* ✅ Sticky Header — top dynamically navbar height se set hota hai */}
      <div
        className={`create-battle-sticky-header pt-3 ${isDarkMode ? "dark" : "light"}`}
        style={{ top: navbarHeight }}
      >
        <div
          className={
            isDarkMode
              ? "page-header dark-mode container custom-container-create-battle"
              : "page-header container custom-container-create-battle"
          }
        >
          <div className="header-left">
            <div onClick={() => navigate(routes.BATTLES)} className="header-back-btn">
              <ChevronLeft  style={{ color: "#A6A6A6" }} size={20} />
            </div>
            <span style={{ marginLeft: "24px" }} className="page-title">
              Create Battle
            </span>
          </div>
          <div className="header-right">
            <div
              className={
                isDarkMode
                  ? "state-label state-label-dark"
                  : "state-label state-label-light"
              }
            >
              Boxes:{" "}<span className="state-label-pr">{totalBoxes}</span>
            </div>
            <div
              className={
                isDarkMode
                  ? "state-label state-label-dark"
                  : "state-label state-label-light"
              }
            >
              Value:{" "}
              <span
                className={
                  isDarkMode
                    ? "state-label-price-dark"
                    : "state-label-price-light"
                }
              >
                {" "}
                ${totalPrice.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Scrollable page content */}
      <div className={isDarkMode ? "page-content dark-mode" : "page-content"}>
        <Container className="p-0 custom-container-create-battle">
          <Col className="mx-auto">
            <div className="create-battle-container">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {(formik) => (
                  <FormikForm id="battle-form">
                    <div
                      className={
                        isDarkMode
                          ? "battle-mode-section dark-mode"
                          : "battle-mode-section"
                      }
                    >
                      <h3 className="section-title">Battle Type</h3>
                      <p className="section-subtitle">
                        Choose your preferred battle type
                      </p>

                      <div className="container-fluid">
                        <div className="row g-4">
                          <div className="col">
                            <div
                              className={`battle-options ${isDarkMode ? "battle-options-dark" : "battle-options-light"} ${formik.values.private_battle ? "selected" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation(); // ✅ yeh add karein
                                handleToggleChange(
                                  formik,
                                  "private_battle",
                                  !formik.values.private_battle,
                                );
                              }}
                            >
                              <div className="option-info">
                                <div className="option-details">
                                  <div className="option-title">
                                    Private Battle
                                  </div>
                                  <div className="option-description">
                                    Invite only battle with friends
                                  </div>
                                </div>
                              </div>
                              <div className="option-controls">
                                <span className="badge badge-blue text-AktivGrotesk-Regular">
                                  Recommended
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col">
                            <div
                              className={`battle-options ${isDarkMode ? "battle-options-dark" : "battle-options-light"} ${formik.values.crazy_mod ? "selected" : ""}`}
                              onClick={() =>
                                handleToggleChange(
                                  formik,
                                  "crazy_mod",
                                  !formik.values.crazy_mod,
                                )
                              }
                            >
                              <div className="option-info">
                                <div className="option-details">
                                  <div className="option-title">Crazy Mode</div>
                                  <div className="option-description">
                                    High stakes public battle arena
                                  </div>
                                </div>
                              </div>
                              <div className="option-controls">
                                <span className="badge badge-red text-AktivGrotesk-Regular">
                                  High Risk
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <span
                      className={
                        isDarkMode
                          ? "divider-line-battle-type"
                          : "divider-line-battle-type-light"
                      }
                    ></span>

                    <div
                      className={
                        isDarkMode
                          ? "box-collection-section dark-mode"
                          : "box-collection-section"
                      }
                    >
                      <h3 className="section-title">Box Collection</h3>
                      <p className="section-subtitle">
                        Add boxes to your battle collection
                      </p>

                      <Container className="p-0" fluid>
                        <Row
                          style={{ marginLeft: "0px", marginRight: "0px" }}
                          className="g-4 pl-0"
                        >
                          <Col md={activeBoxCount > 0 ? 3 : isMobile ? 12 : 3}>
                            <button
                              type="button"
                              className={
                                isDarkMode
                                  ? "add-box-button dark-mode"
                                  : "add-box-button add-box-button-light"
                              }
                              onClick={() => setModalShow(true)}
                            >
                              <AiOutlinePlus
                                className={
                                  isDarkMode
                                    ? "add-box-icon add-box-icon-dark"
                                    : "add-box-icon add-box-icon-light"
                                }
                              />
                              <div className="add-box-title">Add Box</div>
                            </button>
                          </Col>

                          {selectedCards?.map(
                            (item) =>
                              item?.quantity > 0 && (
                                <Col xs={6} md={3} key={item.id}>
                                  <AddBox
                                    item={item}
                                    isDarkMode={isDarkMode}
                                    handleAddToBattleClick={
                                      handleAddToBattleClick
                                    }
                                    handlePlusClick={handlePlusClick}
                                    handleMinusClick={handleMinusClick}
                                    imageContainerClass="image-container--battle-page"
                                  />
                                </Col>
                              ),
                          )}
                        </Row>
                      </Container>
                    </div>
                  </FormikForm>
                )}
              </Formik>
              <div className="d-flex p-lg-0 p-3 justify-content-end align-items-center gap-2 w-100 mb-5">
                <button onClick={() => navigate(routes.BATTLES)} className="deep-primary-monochrome-btn-dark width-crt-btn ">
                  Cancel
                </button>
                <button
                  type="submit"
                  form="battle-form"
                  className={
                    isDarkMode
                      ? "start-battle-button dark-mode"
                      : "start-battle-button light-mode"
                  }
                  disabled={disabled}
                >
                  <div className="start-battle-content">Create Battle</div>
                </button>
              </div>
              <MyVerticallyCenteredModal
                show={modalShow}
                onHide={() => {
                  setModalShow(false);
                  setSelected(null);
                }}
                handleAddToBattleClick={handleAddToBattleClick}
                handlePlusClick={handlePlusClick}
                handleMinusClick={handleMinusClick}
                selected={selected}
                setSelected={setSelected}
                loading={loading}
                setLoading={setLoading}
                data={data}
                totalPrice={totalPrice}
                totalBoxes={totalBoxes}
              />
              <TopUpModal
                show={topupmodalShow}
                setTopUpModalShow={setTopUpModalShow}
              />
            </div>
          </Col>
        </Container>
      </div>
      <BackdropLoader show={loading} />
    </div>
  );
};

export default CreateBoxBattle;